import React from 'react';
import { motion } from 'framer-motion';
import { InfinityIcon, UserRoundCheckIcon, MessagesSquareIcon, BrainIcon, TrophyIcon, MessageCircleIcon, PlaneIcon, VideoIcon, UsersRoundIcon, BriefcaseBusinessIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { NextStep } from '../components/NextStep';
import { academySteps } from '../data/academySteps';
import { Sparkle } from '../components/Sparkle';
import { useSeo } from '../hooks/useSeo';
const stepIcons = [InfinityIcon, UserRoundCheckIcon, MessagesSquareIcon, BrainIcon, TrophyIcon, MessageCircleIcon, PlaneIcon, VideoIcon, UsersRoundIcon, BriefcaseBusinessIcon];
export function Ablauf() {
  useSeo({
    title: 'Ablauf der Academy | Hot Sales Girls Academy',
    description: 'Schritt für Schritt zum Erfolg: So läuft die Hot Sales Girls Academy ab – von den Grundlagen im Closing über wöchentliche Live-Trainings bis zur Begleitung in deinen Remote Job.',
    path: '/ablauf'
  });
  return <div className="min-h-screen w-full overflow-x-hidden" data-id="element-412" data-mp="rtz8iusglkwn">
      <div className="pink-panel relative" data-id="element-413" data-mp="n1fs7q2e0bo5">
        <div className="relative z-10" data-id="element-414" data-mp="n0eh5lldfp6f">
          <Navbar data-id="element-415" data-mp="i2w7tu3fz7xw" />

          <main className="relative w-full bg-cream px-4 pb-2 pt-6 sm:px-6 sm:pb-4 sm:pt-8" data-id="element-416" data-mp="i3o1gzdhavlj">
            <span aria-hidden="true" className="gold-glitter-layer-static" data-id="element-417" data-mp="d4b4hlsw8858" />
            <span aria-hidden="true" className="gold-glitter-extra" data-id="element-418" data-mp="84opp7xf2jub" />
            <div className="relative z-10 mx-auto max-w-[1300px]" data-id="element-419" data-mp="83do9erg1o6d">
              <header className="text-center" data-id="element-420" data-mp="444u48shroaa">
                <span className="mb-6 block text-[11px] font-semibold uppercase tracking-[0.25em] text-hot-500" data-id="element-421" data-mp="44y3lhiu4rw4">
                  Schritt für Schritt
                </span>
                <div className="relative inline-block" data-id="element-422" data-mp="1ygmcb1yh3y82">
                  <Sparkle className="animate-sparkle-delayed bottom-1 right-[3.75rem] h-8 w-8 sm:-right-8 sm:-top-4 sm:bottom-auto sm:h-10 sm:w-10 md:bottom-1 md:right-[28%] md:top-auto lg:-right-8 lg:-top-4 lg:bottom-auto" data-id="element-423" data-mp="1thi4aenac6jb" />
                  <h1 className="font-heading mx-auto max-w-3xl text-3xl font-extrabold uppercase leading-tight text-neutral-900 sm:text-5xl" data-id="element-424" data-mp="2he6c8mh3sqfh">
                    Schritt für Schritt zum Erfolg
                  </h1>
                </div>
                <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-neutral-600 sm:text-base" data-id="element-425" data-mp="28iov5gxppt1o">
                  Das erwartet dich in den 8 Wochen – und weit darüber hinaus.
                </p>
              </header>

              {/* Design 1: durchgehende Zeitleiste links, Karten über die volle Breite. */}
              <div className="relative mx-auto mt-12 max-w-4xl sm:mt-16 lg:mt-20" data-id="element-426" data-mp="2rbaonul5d7j3">
                {/* Durchgehende goldene Linie */}
                <span aria-hidden="true" className="absolute bottom-8 left-5 top-8 w-px bg-gold/40 sm:left-9" data-id="element-427" data-mp="1awj6eahptgni" />

                <ol className="space-y-6 sm:space-y-8" data-id="element-428" data-mp="18cmlf6fqax35">
                  {academySteps.map((step, index) => {
                  const Icon = stepIcons[index];
                  return <motion.li key={step.number} initial={{
                    opacity: 0,
                    y: 16
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-60px'
                  }} transition={{
                    duration: 0.4,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="relative flex items-start gap-4 sm:gap-8" data-id="element-429" data-mp="1hdavxr5wyfba" data-mpi={index}>
                        {/* Nummer auf der Linie */}
                        <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-cream sm:h-[4.5rem] sm:w-[4.5rem]" data-id="element-430" data-mp="to8hstahn4lj">
                          <span className="font-numeral text-gold-3d text-base font-black leading-none sm:text-2xl" data-id="element-431" data-mp="5w1e192fnvc3">
                            {step.number}
                          </span>
                        </span>

                        <div className="flex-1 rounded-3xl border border-gold/20 bg-white/70 p-5 shadow-sm backdrop-blur-[2px] sm:p-8" data-id="element-432" data-mp="jkv0k1xaeaej">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-hot-50" data-id="element-433" data-mp="ojfsle2dxnrw">
                            <Icon className="h-5 w-5 text-hot-500" aria-hidden="true" data-id="element-434" data-mp="tc8xq31rmy4i" />
                          </span>

                          <h2 className="font-heading mt-4 text-lg font-bold leading-snug text-neutral-900 sm:mt-6 sm:text-2xl" data-id="element-435" data-mp="xevqc669gn2r">
                            {step.title}
                          </h2>

                          <p className="mt-3 text-sm leading-relaxed text-neutral-600 sm:text-base" data-id="element-436" data-mp="178uo6szaxkow">
                            {step.text}
                          </p>
                        </div>
                      </motion.li>;
                })}
                </ol>
              </div>
            </div>
          </main>

          <NextStep data-id="element-437" data-mp="1v72f4z65utqj" />

          <Footer data-id="element-438" data-mp="1v7bwjyd53skm" />
        </div>
      </div>
      <CookieBanner data-id="element-439" data-mp="1qfke6b450t5k" />
    </div>;
}